import { ErrorBoundaryRetry } from "@package/ui-components";
import { EventPayloadType } from "@package/ui-gql/index-analytics";
import { useSelectorWall } from "@package/ui-redux";
import {
  type ErrorInfo,
  type PropsWithChildren,
  useEffect,
  useMemo,
} from "react";
import { type ScreenDimension } from "../constants/constants";
import { useWallAnalytics } from "../context/context-analytics/context-analytics";
import { WallAnalyticsProvider } from "../context/context-analytics/context-analytics-provider";
import { useWallProvider } from "../context/context-wall/context-wall";
import { getWallDimensions } from "../utils/get-screen-dimensions";
import { BackgroundMedia } from "./BackgroundMedia/BackgroundMedia";
import { CachingIndicator } from "./CachingIndicator/CachingIndicator";
import { RestingMedia } from "./RestingMedia/RestingMedia";
import { Screens } from "./Screens/Screens";
import { VideoInput } from "./VideoInput/VideoInput";
import { WallError } from "./WallError/WallError";
import { WallLoading } from "./WallLoading/WallLoading";

function WallShell({ children }: PropsWithChildren) {
  const { trigger } = useWallAnalytics();

  useEffect(
    function effectOnWallStarted() {
      trigger(EventPayloadType.DeviceStarted, {});
    },
    [trigger],
  );

  return <>{children}</>;
}

function WallContainer({ children }: PropsWithChildren) {
  const type = useSelectorWall((state) => state.wall.type);
  const profileScreen = useSelectorWall((state) => state.wall.profileScreen);
  const dimensionsWall = useMemo<ScreenDimension>(
    () => getWallDimensions(type, profileScreen),
    [profileScreen, type],
  );

  return (
    <div className="flex flex-col items-center justify-center h-full w-full bg-black bp6-dark">
      <div className="relative overflow-hidden" style={{ ...dimensionsWall }}>
        {children}
      </div>
    </div>
  );
}

type WallProps = {
  onError?: (error: Error, info: ErrorInfo) => void;
};

export function Wall({ onError }: WallProps) {
  const initialization = useSelectorWall((state) => state.wall.initalization);
  const provider = useWallProvider();

  if (
    initialization === "denied" ||
    initialization === "failure" ||
    initialization === "activation-required"
  ) {
    return (
      <WallContainer>
        <WallError />
      </WallContainer>
    );
  }

  if (
    initialization === "starting" ||
    initialization === "activating" ||
    initialization === "loading"
  ) {
    return (
      <WallContainer>
        <WallLoading />
      </WallContainer>
    );
  }

  return (
    <ErrorBoundaryRetry
      onReset={() => provider?.onReset()}
      onError={(error, info) => {
        console.error("Wall error:", error, info);
        onError?.(error, info);
      }}
    >
      <WallAnalyticsProvider>
        <WallShell>
          <WallContainer>
            <BackgroundMedia />
            <Screens />
            <RestingMedia />
            <VideoInput />
            <CachingIndicator />
          </WallContainer>
        </WallShell>
      </WallAnalyticsProvider>
    </ErrorBoundaryRetry>
  );
}
